import { useState, useEffect, useRef } from 'react';
import firebase from 'firebase/app';
import 'firebase/messaging';
import { api } from 'js/utility/api';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';
import { actions as notiActions } from '../../redux/modules/notificaciones/notificaciones';

// Configuración de Firebase
const firebaseConfig = {
    apiKey: "AIzaSyDSwUF1lrI8agEILnmwHKLwLSc76CIj9t8",
    authDomain: "sigem-asonbomd-5208.firebaseapp.com",
    projectId: "sigem-asonbomd-5208",
    storageBucket: "sigem-asonbomd-5208.firebasestorage.app",
    messagingSenderId: "1077591719567",
    appId: "1:1077591719567:web:52a423bfcf684a2312bafa",
    measurementId: "G-W668C58T5P"
};

// VAPID Key para Web Push
const VAPID_KEY = "BJJ0YFwGIpybR2YsKH1RGKMn7h4TLPL7PXoVeIw_ZC8CZ-_7n4Lp2vXckH_G1QRq4Cvd3ev7wtYmFCwbBgc2wlQ";

const isMobile = () => {
    const toMatch = [
        /Android/i, /webOS/i, /iPhone/i, /iPad/i, /iPod/i,
        /BlackBerry/i, /Windows Phone/i
    ];
    return toMatch.some((toMatchItem) => navigator.userAgent.match(toMatchItem));
};

/**
 * Hook para gestionar notificaciones push
 * - Solicita permisos al usuario (manualmente)
 * - Registra token en el backend
 * - Escucha notificaciones en primer plano
 * - Refresca el conteo de no-leídas cada 30 segundos (red safety net)
 */
export const useNotifications = (onNewMessage = null) => {
    const dispatch = useDispatch();
    const [permission, setPermission] = useState('default');
    const [isRegistered, setIsRegistered] = useState(true);
    const [unreadCount, setUnreadCount] = useState(0);

    // Store onNewMessage in a ref so the Firebase subscriber never becomes stale
    // without needing to re-subscribe on every render.
    const onNewMessageRef = useRef(onNewMessage);
    useEffect(() => {
        onNewMessageRef.current = onNewMessage;
    }, [onNewMessage]);

    // Tracks the last known unread count to detect new arrivals from polling
    const lastCountRef = useRef(null);

    // Read current browser permission on mount
    useEffect(() => {
        if ('Notification' in window) {
            setPermission(Notification.permission);
        }
    }, []);

    const initFirebase = () => {
        if (!firebase.apps.length) {
            firebase.initializeApp(firebaseConfig);
        }
    };

    const registerTokenInBackend = async () => {
        try {
            console.log('🔄 Iniciando registro de token FCM...');
            initFirebase();
            const messaging = firebase.messaging();
            const currentToken = await messaging.getToken({ vapidKey: VAPID_KEY });

            if (currentToken) {
                console.log(`🎫 Token FCM obtenido:`, currentToken.substring(0, 10) + '...');
                let device_type = 'web';
                const ua = navigator.userAgent;
                if (/Android/i.test(ua)) device_type = 'android';
                else if (/iPhone|iPad|iPod/i.test(ua)) device_type = 'ios';

                const response = await api.post('/notification/register_token', {
                    token: currentToken,
                    device_type: device_type
                });
                console.log(`✅ Token FCM (${device_type}) sincronizado.`, response);
                return currentToken;
            } else {
                console.warn('⚠️ getToken retornó vacío.');
            }
            return null;
        } catch (error) {
            console.error('❌ Error sincronizando token FCM:', error);
            return null;
        }
    };

    const checkTokenRegistry = async (token = null) => {
        try {
            console.log('🔍 Verificando registro de token en el servidor...');
            const url = token
                ? `/notification/check_token?token=${encodeURIComponent(token)}`
                : '/notification/check_token';
            const response = await api.get(url);

            const registered = response && response.has_token === true;

            setIsRegistered(registered);

            if (!registered && !sessionStorage.getItem('token_check_notified')) {
                Swal.fire({
                    title: 'Sincronización de Notificaciones',
                    text: 'No hemos detectado un registro de notificaciones para tu cuenta en este dispositivo. Cierra sesión e inicia sesión de nuevo.',
                    icon: 'warning',
                    confirmButtonText: 'Entendido',
                    confirmButtonColor: '#0F45C7',
                });
                sessionStorage.setItem('token_check_notified', 'true');
            }
        } catch (error) {
            console.error('❌ Error verificando registro de token:', error);
        }
    };

    const requestPermission = async () => {
        if (!('Notification' in window)) return;

        try {
            const result = await Notification.requestPermission();
            setPermission(result);

            if (result === 'granted') {
                const token = await registerTokenInBackend();
                if (token) setIsRegistered(true);
            } else {
                setIsRegistered(false);
            }
        } catch (error) {
            console.error('Error al solicitar permisos:', error);
        }
    };

    const resetCount = () => setUnreadCount(0);

    const fetchUnreadCount = async ({ showToastOnNew = false } = {}) => {
        try {
            const response = await api.get('/notification/unread_count');
            if (response && response.count !== undefined) {
                const newCount = response.count;
                const prevCount = lastCountRef.current;

                setUnreadCount(newCount);

                // If polling and count increased, show toast for the new notification(s)
                if (showToastOnNew && prevCount !== null && newCount > prevCount) {
                    console.log(`🔔 Polling detectó ${newCount - prevCount} nueva(s) notificación(es)`);
                    try {
                        const listResponse = await api.get('/notification/list_notifications?page=1&page_size=1');
                        const latest = listResponse && listResponse.results && listResponse.results[0];
                        if (latest) {
                            const emergencyId = latest.data && latest.data.emergency_id;
                            Swal.fire({
                                title: latest.title || '🚨 Nueva Emergencia',
                                text: latest.body || 'Se ha creado una nueva emergencia',
                                icon: 'info',
                                position: isMobile() ? 'top' : 'top-end',
                                toast: true,
                                showConfirmButton: false,
                                timer: 6000,
                                timerProgressBar: true,
                                showCloseButton: true,
                                background: '#fff',
                                color: '#545454',
                                iconColor: '#0F45C7',
                                padding: '1em',
                                customClass: {
                                    popup: 'animated fadeInDown faster notification-toast-custom',
                                    title: 'notification-toast-title',
                                    content: 'notification-toast-content'
                                },
                                didOpen: (toast) => {
                                    toast.addEventListener('click', (e) => {
                                        if (!e.target.classList.contains('swal2-close') && emergencyId) {
                                            window.location.hash = `#/emergency/${emergencyId}`;
                                        }
                                    });
                                    toast.addEventListener('mouseenter', Swal.stopTimer);
                                    toast.addEventListener('mouseleave', Swal.resumeTimer);
                                }
                            });
                            // Trigger global reactivity
                            dispatch(notiActions.triggerRealtimeSignal());
                            if (onNewMessageRef.current) onNewMessageRef.current({ notification: latest });
                        }
                    } catch (e) {
                        console.error('Error fetching latest notification for toast:', e);
                    }
                }

                lastCountRef.current = newCount;
            }
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    };

    // Effect 1: Token registration — runs once when permission is granted
    useEffect(() => {
        if (permission !== 'granted') {
            if (permission === 'denied' || permission === 'default') {
                setIsRegistered(false);
            }
            return;
        }

        const syncToken = async (retries = 3) => {
            try {
                const token = await registerTokenInBackend();
                if (token) {
                    setIsRegistered(true);
                    console.log('✨ Registro automático completado.');
                    return;
                }
            } catch (err) {
                console.error(`Falla en sincronización (${retries}):`, err);
            }

            if (retries > 0) {
                setTimeout(() => syncToken(retries - 1), 3000);
            } else {
                checkTokenRegistry();
            }
        };

        syncToken();
    }, [permission]);

    // Effect 2: Unread count — fetch immediately + poll every 30 seconds as safety net
    // On first load: show a summary toast if there are accumulated unread notifications,
    // then set that count as the stable baseline. Polling after that only toasts for NEW increments.
    useEffect(() => {
        if (permission !== 'granted') return;

        // First call: fetch current count.
        // If there are already unread notifications (e.g. missed while push wasn't working),
        // show a one-time summary toast, then lock in baseline so polling only reacts to NEW ones.
        const initialFetch = async () => {
            try {
                const response = await api.get('/notification/unread_count');
                if (response && response.count !== undefined) {
                    const count = response.count;
                    setUnreadCount(count);

                    if (count > 0) {
                        // Show a summary toast for accumulated unread notifications
                        Swal.fire({
                            title: `🔔 Tienes ${count} notificación${count > 1 ? 'es' : ''} pendiente${count > 1 ? 's' : ''}`,
                            text: 'Abre la campanita para ver tus notificaciones.',
                            icon: 'info',
                            position: isMobile() ? 'top' : 'top-end',
                            toast: true,
                            showConfirmButton: false,
                            timer: 7000,
                            timerProgressBar: true,
                            showCloseButton: true,
                            background: '#fff',
                            color: '#545454',
                            iconColor: '#0F45C7',
                            padding: '1em',
                            customClass: {
                                popup: 'animated fadeInDown faster notification-toast-custom',
                                title: 'notification-toast-title',
                            },
                        });
                    }

                    // Set baseline AFTER showing initial toast — subsequent polling
                    // only shows toast when count goes ABOVE this baseline.
                    lastCountRef.current = count;
                }
            } catch (error) {
                console.error('Error fetching initial unread count:', error);
            }
        };

        initialFetch();

        // Polling: check every 30s and show toast only if count increased from baseline
        const interval = setInterval(() => {
            fetchUnreadCount({ showToastOnNew: true });
        }, 30000);

        return () => clearInterval(interval);
    }, [permission]);


    // Effect 3: Firebase foreground onMessage listener
    // Stable: only re-subscribes when permission changes, NOT on every onNewMessage callback change.
    // The ref pattern keeps the callback fresh without triggering re-subscription.
    useEffect(() => {
        if (permission !== 'granted') return;

        initFirebase();
        const messaging = firebase.messaging();

        const unsubscribe = messaging.onMessage((payload) => {
            console.log('🔔 Mensaje recibido en primer plano:', payload);

            fetchUnreadCount({ showToastOnNew: false });
            dispatch(notiActions.triggerRealtimeSignal());

            if (onNewMessageRef.current) {
                onNewMessageRef.current(payload);
            }

            const title = (payload.notification && payload.notification.title) || '🚨 Nueva Emergencia';
            const body = (payload.notification && payload.notification.body) || 'Se ha creado una nueva emergencia';
            const emergencyId = payload.data && payload.data.emergency_id;

            Swal.fire({
                title,
                text: body,
                icon: 'info',
                position: isMobile() ? 'top' : 'top-end',
                toast: true,
                showConfirmButton: false,
                timer: 6000,
                timerProgressBar: true,
                showCloseButton: true,
                background: '#fff',
                color: '#545454',
                iconColor: '#0F45C7',
                padding: '1em',
                customClass: {
                    popup: 'animated fadeInDown faster notification-toast-custom',
                    title: 'notification-toast-title',
                    content: 'notification-toast-content'
                },
                didOpen: (toast) => {
                    toast.addEventListener('click', (e) => {
                        if (!e.target.classList.contains('swal2-close') && emergencyId) {
                            window.location.hash = `#/emergency/${emergencyId}`;
                        }
                    });
                    toast.addEventListener('mouseenter', Swal.stopTimer);
                    toast.addEventListener('mouseleave', Swal.resumeTimer);
                }
            });
        });

        return () => {
            unsubscribe && unsubscribe();
        };
    }, [permission]); // Only re-subscribe when permission changes

    // Effect 4: Listen for messages from the Service Worker
    // When a desktop notification is clicked and the app is already open,
    // the SW sends a postMessage({ type: 'NOTIFICATION_CLICK', url, data }).
    // We intercept it here to navigate without a full page reload.
    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;

        const handleSWMessage = (event) => {
            if (!event.data) return;

            if (event.data.type === 'NOTIFICATION_CLICK') {
                const { url, data } = event.data;
                console.log('[useNotifications] SW postMessage NOTIFICATION_CLICK:', url, data);

                if (url) {
                    // Extract the hash portion (e.g. "#/emergency/42") and navigate
                    try {
                        const urlObj = new URL(url);
                        const hash = urlObj.hash; // e.g. "#/emergency/42"
                        if (hash) {
                            window.location.hash = hash;
                        } else {
                            window.location.href = url;
                        }
                    } catch (e) {
                        window.location.href = url;
                    }
                }

                // Also refresh the unread count so the bell updates
                fetchUnreadCount({ showToastOnNew: false });
            }
        };

        navigator.serviceWorker.addEventListener('message', handleSWMessage);
        return () => {
            navigator.serviceWorker.removeEventListener('message', handleSWMessage);
        };
    }, []); // mount once

    return { permission, requestPermission, unreadCount, resetCount, fetchUnreadCount, isRegistered };
};


/**
 * Función para desregistrar token al cerrar sesión
 */
export const unregisterNotificationToken = async () => {
    try {
        if (!firebase.apps.length) {
            firebase.initializeApp(firebaseConfig);
        }
        const messaging = firebase.messaging();
        const currentToken = await messaging.getToken({ vapidKey: VAPID_KEY });

        if (currentToken) {
            await api.post('/notification/unregister_token', { token: currentToken });
            console.log('Token desregistrado');
        }
    } catch (error) {
        console.error('Error desregistrando token:', error);
    }
};
